import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Minus, Plus, Sparkles } from "lucide-react";
import { toast } from "sonner";

const todayISO = () => new Date(Date.now() + 86400000).toISOString().slice(0, 10);

// Common dinner / lunch booking slots — we generate every 30 minutes between
// 11:30-15:00 and 18:30-23:00 to cover most use-cases. Format: "HH:MM".
const TIME_SLOTS = (() => {
  const out = [];
  const addRange = (startHr, startMin, endHr, endMin) => {
    let h = startHr, m = startMin;
    while (h < endHr || (h === endHr && m <= endMin)) {
      out.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
      m += 30;
      if (m >= 60) { h += 1; m -= 60; }
    }
  };
  addRange(11, 30, 15, 0);    // lunch
  addRange(18, 30, 23, 0);    // dinner
  return out;
})();

// Pretty AM/PM label, e.g. "07:30 PM"
const labelSlot = (s) => {
  const [h, m] = s.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return `${String(h12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${period}`;
};

export default function Home() {
  const nav = useNavigate();
  const [meta, setMeta] = useState({ areas: [], vibes: [], purposes: [] });
  const [mode, setMode] = useState("dining"); // "dining" (5-40) | "takeaway" (10-200)
  const [pax, setPax] = useState(12);
  const [budget, setBudget] = useState(1500);
  const [area, setArea] = useState("");
  const [vibe, setVibe] = useState("");
  const [purpose, setPurpose] = useState("");
  const [date, setDate] = useState(todayISO());
  const [time, setTime] = useState("20:00");
  const [diningPref, setDiningPref] = useState("");
  const [loading, setLoading] = useState(false);

  // Switching modes auto-clamps pax to the mode's natural range.
  const setModeAndClamp = (m) => {
    setMode(m);
    if (m === "takeaway") {
      if (pax < 10) setPax(10);
      if (pax > 200) setPax(50);
    } else if (m === "dining") {
      if (pax > 40) setPax(12);
      if (pax < 5) setPax(5);
    }
  };

  const minPax = mode === "takeaway" ? 10 : 5;
  const maxPax = mode === "takeaway" ? 200 : 40;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/meta");
        setMeta(data);
        if (!area && data.areas?.length) setArea(data.areas[0]);
      } catch (e) {
        // ignore
      }
    })();
  }, []); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    if (!area) return toast.error("Pick an area");
    setLoading(true);
    try {
      const { data } = await api.post("/discovery", {
        pax,
        budget_per_head: budget,
        area,
        date,
        time,
        vibe: vibe || null,
        dining_model_pref: diningPref || null,
        free_text: "",
        mode,
      });
      sessionStorage.setItem("tablo_results", JSON.stringify(data.results));
      sessionStorage.setItem("tablo_brief", JSON.stringify({ pax, budget, area, vibe, purpose, date, time, diningPref, mode }));
      nav("/results");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Discovery failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-7 animate-fade-up">
      <header>
        <p className="text-amber text-xs uppercase tracking-[0.18em] font-medium mb-2">Plan an outing</p>
        <h1 className="font-serif text-3xl sm:text-4xl leading-tight">A few details and we'll do the rest.</h1>
        <p className="text-light-ink text-sm mt-2">Tell us how many you are, your budget, and the vibe. We'll match you to 3–5 Hyderabad venues with locked package prices.</p>
      </header>

      <form onSubmit={submit} className="space-y-5">
        {/* Mode switch: Dining vs Banquet */}
        <div className="tablo-card p-2 flex" data-testid="home-mode-switch">
          {[
            { v: "dining", label: "Group dine-in (5–40)", hint: "Sit-down at the venue" },
            { v: "takeaway", label: "Takeaway / large order (10+)", hint: "Pick up or send a runner" },
          ].map((m) => (
            <button
              key={m.v}
              type="button"
              onClick={() => setModeAndClamp(m.v)}
              className={`flex-1 text-sm rounded-full py-2.5 px-3 transition-colors ${mode === m.v ? "bg-ink text-cream" : "text-light-ink hover:text-ink"}`}
              data-testid={`home-mode-${m.v}`}
            >
              <span className="font-medium">{m.label}</span>
              <span className="block text-[10px] uppercase tracking-wider mt-0.5 opacity-70">{m.hint}</span>
            </button>
          ))}
        </div>
        {mode === "takeaway" && (
          <div className="rounded-xl bg-amber-soft/30 border border-amber/40 px-4 py-3 text-xs text-ink" data-testid="home-takeaway-banner">
            <strong>Takeaway mode:</strong> Tablo curates a balanced bulk menu for the group. You collect from the restaurant at your slot, or send a runner. No table reservation needed.
          </div>
        )}

        {/* Pax stepper */}
        <div className="tablo-card p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider text-light-ink">Group size</p>
              <p className="font-serif text-2xl">{pax} guests</p>
              <p className="text-[11px] text-light-ink/70">{mode === "takeaway" ? "Takeaway · Min 10 · Max 200" : "Dine-in · Min 5 · Max 40"}</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setPax(Math.max(minPax, pax - (mode === "takeaway" ? 5 : 1)))}
                className="w-10 h-10 rounded-full bg-cream border border-tablo-border flex items-center justify-center hover:bg-tablo-border/30 disabled:opacity-30"
                data-testid="home-pax-decr"
                disabled={pax <= minPax}
              >
                <Minus className="w-4 h-4" strokeWidth={1.6} />
              </button>
              <button
                type="button"
                onClick={() => setPax(Math.min(maxPax, pax + (mode === "takeaway" ? 5 : 1)))}
                className="w-10 h-10 rounded-full bg-ink text-cream flex items-center justify-center hover:bg-light-ink disabled:opacity-30"
                data-testid="home-pax-incr"
                disabled={pax >= maxPax}
              >
                <Plus className="w-4 h-4" strokeWidth={1.8} />
              </button>
            </div>
          </div>
        </div>

        {/* Budget slider */}
        <div className="tablo-card p-5">
          <div className="flex items-baseline justify-between mb-3">
            <p className="text-xs uppercase tracking-wider text-light-ink">Budget per head</p>
            <p className="font-serif text-2xl text-ink">₹{budget.toLocaleString("en-IN")}</p>
          </div>
          <Slider
            value={[budget]}
            onValueChange={(v) => setBudget(v[0])}
            min={500}
            max={4000}
            step={100}
            className="my-2"
            data-testid="home-budget-slider"
          />
          <div className="flex justify-between text-[11px] text-light-ink/70 mt-1">
            <span>₹500</span>
            <span>₹4,000</span>
          </div>
        </div>

        {/* Area + date + time */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-light-ink mb-1 block">Area</label>
            <Select value={area} onValueChange={setArea}>
              <SelectTrigger className="bg-white border-tablo-border" data-testid="home-area-select"><SelectValue placeholder="Pick" /></SelectTrigger>
              <SelectContent>
                {meta.areas.map((a) => <SelectItem key={a} value={a} data-testid={`home-area-${a}`}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-light-ink mb-1 block">Date</label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="bg-white border-tablo-border" data-testid="home-date-input" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-light-ink mb-1 block">Time</label>
            <Select value={time} onValueChange={setTime}>
              <SelectTrigger className="bg-white border-tablo-border" data-testid="home-time-input">
                <SelectValue placeholder="Pick a slot" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-light-ink/70">Lunch</div>
                {TIME_SLOTS.filter((s) => s < "16:00").map((s) => (
                  <SelectItem key={s} value={s} data-testid={`home-time-opt-${s}`}>{labelSlot(s)}</SelectItem>
                ))}
                <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-light-ink/70">Dinner</div>
                {TIME_SLOTS.filter((s) => s >= "16:00").map((s) => (
                  <SelectItem key={s} value={s} data-testid={`home-time-opt-${s}`}>{labelSlot(s)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Purpose chips */}
        <div>
          <p className="text-xs uppercase tracking-wider text-light-ink mb-2">Booking for</p>
          <div className="flex flex-wrap gap-2">
            {(meta.purposes || ["Office", "Family", "Friends", "Date", "Other"]).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPurpose(purpose === p ? "" : p)}
                className={`tablo-chip ${purpose === p ? "tablo-chip-selected" : "tablo-chip-unselected"}`}
                data-testid={`home-purpose-${p.toLowerCase()}`}
              >
                {p}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-light-ink/70 mt-2">Pick once per booking — same Tablo account can book for office, family, or friends.</p>
        </div>

        {/* Dining model filter */}
        <div>
          <p className="text-xs uppercase tracking-wider text-light-ink mb-2">How do you want to order?</p>
          <div className="flex flex-wrap gap-2" data-testid="home-dining-filter">
            {[
              { v: "", label: "Any" },
              { v: "package_only", label: "Package only" },
              { v: "a_la_carte", label: "À-la-carte" },
            ].map((opt) => (
              <button
                key={opt.v || "any"}
                type="button"
                onClick={() => setDiningPref(opt.v)}
                className={`tablo-chip ${diningPref === opt.v ? "tablo-chip-selected" : "tablo-chip-unselected"}`}
                data-testid={`home-dining-${opt.v || "any"}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-light-ink/70 mt-2">Packages = chef-curated set meal · À-la-carte = you (or Tablo Curate) pick from the full menu within budget.</p>
        </div>

        {/* Vibe chips */}
        <div>
          <p className="text-xs uppercase tracking-wider text-light-ink mb-2">Vibe</p>
          <div className="flex flex-wrap gap-2">
            {meta.vibes.map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setVibe(vibe === v ? "" : v)}
                className={`tablo-chip ${vibe === v ? "tablo-chip-selected" : "tablo-chip-unselected"}`}
                data-testid={`home-vibe-${v.toLowerCase()}`}
              >
                {v}
              </button>
            ))}
          </div>
        </div>

        <Button type="submit" disabled={loading} className="w-full tablo-btn-primary" data-testid="home-submit-btn">
          <Sparkles className="w-4 h-4 mr-2" strokeWidth={1.6} />
          {loading ? "Curating matches..." : "Find my venue"}
        </Button>
      </form>
    </div>
  );
}
